import { useSuspenseQuery } from '@tanstack/react-query';
import type { AdsHistoryGroupedEntry } from '../components/AdsHistoryGroupedView.mockData';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import type { ListResponse, MarketingProcess } from 'types/shared';
import { apiClient } from 'lib/api';

interface AdsHistoryPackRepresentative {
  created_by: string;
  created_at: string;
  ad_account: { name: string; id: string };
  adset: { name: string; id: string };
  message: string;
  status: boolean;
}

interface AdsHistoryPackAd extends Omit<AdsHistoryGroupedEntry, 'pack_name' | 'hypothesis' | 'email' | 'ad_set' | 'created_at'> {
  ad_id: string;
}

interface AdsHistoryPackGroup {
  pack_name: string;
  representative: AdsHistoryPackRepresentative;
  ads: AdsHistoryPackAd[];
  total_ads: number;
}

interface AdsHistoryPacksWeek {
  week_key: string;
  label: string;
  packs: AdsHistoryPackGroup[];
}

export type AdsHistoryPacksResponse = ListResponse<AdsHistoryPacksWeek>;

export interface GetAdsHistoryPacksParams {
  marketing_process?: MarketingProcess;
  limit?: number;
  offset?: number;
  user?: string;
  status?: boolean;
  flow?: string;
  hypothesis?: string;
  customer?: string;
  technology?: string;
}

export const getAdsHistoryPacks = async (
  params?: GetAdsHistoryPacksParams,
  signal?: AbortSignal,
): Promise<AdsHistoryPacksResponse> => {
  const res = await apiClient.get<AdsHistoryPacksResponse>(queryKeys.adsHistoryPacks, {
    signal,
    params,
  });

  return res.data;
};

interface UseAdsHistoryPacksOptions<TData> {
  config?: QueryConfig<typeof getAdsHistoryPacks, TData>;
  params?: GetAdsHistoryPacksParams;
}

export const useAdsHistoryPacks = <TData = AdsHistoryPacksResponse>(
  { config, params }: UseAdsHistoryPacksOptions<TData> = {},
) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getAdsHistoryPacks(params, signal),
    queryKey: [queryKeys.adsHistoryPacks, params],
  });
};
